/**
 * PATH: packages/drafting/src/hooks/useAiActions.ts
 * FILE_ID: DRAFTING-HOOK-AIACTIONS-001
 * PURPOSE: React state wrapper around the AI runner (ai/runner.ts). Owns the
 *   busy/status/error lifecycle and funnels a successful generate/refine result
 *   back through `replaceIr` so AI output shares the SAME IR + undo/redo as
 *   manual edits (useDrafting). Keeps DraftingStudio thin.
 */
import { useCallback, useMemo, useState } from 'react'
import type { DrawingIR } from '../ir/schema'
import type { DraftingAiHandlers, DraftingMeasurement } from '../ai/types'
import { runGenerate, runRefine } from '../ai/runner'

export interface AiActions {
  generating: boolean
  status: string | null
  error: string | null
  generate: (prompt: string, sketch: File | null, measurements?: DraftingMeasurement[]) => Promise<void>
  refine: (instruction: string) => Promise<void>
  clearError: () => void
}

interface UseAiActionsOpts extends DraftingAiHandlers {
  scale: string
  units: DrawingIR['meta']['units']
  currentIr: DrawingIR
  replaceIr: (ir: DrawingIR) => void
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message
  const ax = e as { response?: { data?: { detail?: string } }; message?: string }
  return ax?.response?.data?.detail || ax?.message || 'AI request failed'
}

export function useAiActions(opts: UseAiActionsOpts): AiActions {
  const { scale, units, currentIr, replaceIr, onGenerate, onRefine, onUploadSketch } = opts
  const [generating, setGenerating] = useState(false)
  const [status, setStatus] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const deps = useMemo(
    () => ({ onGenerate, onRefine, onUploadSketch, setStatus }),
    [onGenerate, onRefine, onUploadSketch],
  )

  const generate = useCallback(
    async (prompt: string, sketch: File | null, measurements?: DraftingMeasurement[]) => {
      setError(null)
      setGenerating(true)
      try {
        const result = await runGenerate(deps, { prompt, sketch, scale, units, measurements })
        replaceIr(result.ir)
      } catch (e) {
        setError(messageOf(e))
        setStatus(null)
      } finally {
        setGenerating(false)
      }
    },
    [deps, scale, units, replaceIr],
  )

  const refine = useCallback(
    async (instruction: string) => {
      setError(null)
      setGenerating(true)
      try {
        const result = await runRefine(deps, { instruction, baseIr: currentIr })
        replaceIr(result.ir)
      } catch (e) {
        setError(messageOf(e))
        setStatus(null)
      } finally {
        setGenerating(false)
      }
    },
    [deps, currentIr, replaceIr],
  )

  const clearError = useCallback(() => setError(null), [])

  return { generating, status, error, generate, refine, clearError }
}
